import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { LCShieldLogo } from "@/components/LCShieldLogo";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { setLicenseEmail } from "@/lib/history";
import { useI18n } from "@/i18n";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-lc-navy text-white font-sans flex items-center justify-center px-6">
    <Seo path="/payment" title="Payment | LC Shield" description="Complete your LC Shield subscription and unlock unlimited UCP 600 discrepancy checks." />
    <div className="w-full max-w-md rounded-sm border border-white/10 bg-lc-slate/40 p-10 text-center">
      <div className="flex items-center justify-center gap-2 mb-8">
        <LCShieldLogo className="text-lc-sky" size={24} />
        <span className="font-serif text-xl">LC Shield</span>
      </div>
      {children}
    </div>
  </div>
);

export function PaymentSuccess() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) { setState("error"); return; }
    try {
      const r = await api.get(`/payments/status/${sessionId}`);
      if (r.data.payment_status === "paid") {
        if (r.data.customer_email) setLicenseEmail(r.data.customer_email.toLowerCase());
        setState("paid");
      }
      else if (["expired", "failed"].includes(r.data.payment_status)) setState("error");
      else if (attempts >= 8) setState("pending");
      else setTimeout(() => setAttempts((a) => a + 1), 2000);
    } catch {
      if (attempts >= 8) setState("error");
      else setTimeout(() => setAttempts((a) => a + 1), 2000);
    }
  }, [sessionId, attempts]);

  useEffect(() => { if (state === "checking") poll(); }, [attempts, state, poll]);

  return (
    <Shell>
      {state === "checking" && (
        <div data-testid="payment-checking">
          <CircleNotch weight="bold" size={44} className="mx-auto text-lc-sky animate-spin" />
          <h1 className="mt-6 font-serif text-2xl">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-lc-fog">Verifying with Stripe. This takes a few seconds.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle weight="duotone" size={52} className="mx-auto text-lc-ok" />
          <h1 className="mt-6 font-serif text-3xl">You're in.</h1>
          <p className="mt-2 text-sm text-lc-fog">Your LC Shield access is active. Open the workspace to run your first presentation check.</p>
          <button onClick={() => nav("/workspace")} data-testid="go-workspace-btn"
            className="mt-8 w-full rounded-sm bg-lc-sky px-5 py-3 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">
            Open Workspace
          </button>
        </div>
      )}
      {state === "pending" && (
        <div data-testid="payment-pending">
          <CircleNotch weight="bold" size={44} className="mx-auto text-lc-warn" />
          <h1 className="mt-6 font-serif text-2xl">Still processing</h1>
          <p className="mt-2 text-sm text-lc-fog">Your payment is taking longer than usual. You can safely check back shortly.</p>
          <button onClick={() => nav("/")} className="mt-8 w-full rounded-sm border border-white/20 px-5 py-3 text-sm font-semibold hover:bg-white/5 transition-colors duration-200">Back home</button>
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-error">
          <XCircle weight="duotone" size={52} className="mx-auto text-lc-critical" />
          <h1 className="mt-6 font-serif text-2xl">Payment not confirmed</h1>
          <p className="mt-2 text-sm text-lc-fog">We couldn't verify this payment. No charge may have been made.</p>
          <button onClick={() => nav("/#pricing")} className="mt-8 w-full rounded-sm border border-white/20 px-5 py-3 text-sm font-semibold hover:bg-white/5 transition-colors duration-200">Back to pricing</button>
        </div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  const { t } = useI18n();
  return (
    <Shell>
      <div data-testid="payment-cancel">
        <XCircle weight="duotone" size={52} className="mx-auto text-lc-fog" />
        <h1 className="mt-6 font-serif text-2xl">{t("pay.cancelTitle")}</h1>
        <p className="mt-2 text-sm text-lc-fog">{t("pay.cancelSub")}</p>
        <div className="mt-8 flex gap-3">
          <button onClick={() => nav("/#pricing")} data-testid="cancel-pricing-btn"
            className="flex-1 rounded-sm border border-white/20 px-5 py-3 text-sm font-semibold hover:bg-white/5 transition-colors duration-200">{t("pay.pricingBtn")}</button>
          <button onClick={() => nav("/#demo")} data-testid="cancel-workspace-btn"
            className="flex-1 rounded-sm bg-lc-sky px-5 py-3 text-sm font-semibold text-lc-navy hover:bg-white transition-colors duration-200">{t("pay.workspaceBtn")}</button>
        </div>
      </div>
    </Shell>
  );
}
